import { mainFaqMap } from './faqs/faq-data-map';
import { HelpPages, TopicSidebars } from './info-navigation-map';

export type HelpSearchResult = {
  title: string;
  href: string;
  /** Which part of the Help Center the result lives in. */
  kind: 'How To' | 'Admin' | 'FAQ' | 'Help';
  /** External links (PDFs) open in a new tab rather than routing. */
  external?: boolean;
  /** A short excerpt of the matching body text, when there is one. */
  snippet?: string;
};

type IndexEntry = HelpSearchResult & {
  titleLower: string;
  body: string;
  bodyLower: string;
};

/** Maximum results shown for a query. */
export const MAX_HELP_SEARCH_RESULTS = 8;

/**
 * Display order for results: how to guides first, then FAQs, then anything
 * else, with admin pages last. Most people searching the Help Center want the
 * how to page, not the admin equivalent or the FAQ that mentions it.
 */
const KIND_RANK: Record<HelpSearchResult['kind'], number> = {
  'How To': 0,
  FAQ: 1,
  Help: 2,
  Admin: 3,
};

/** Section headings, matching the names used in the Help Center nav. */
const KIND_HEADING: Record<HelpSearchResult['kind'], string> = {
  'How To': 'How To Guides',
  FAQ: 'Frequently Asked Questions',
  Help: 'Help',
  Admin: 'Admin',
};

/** Results for one section of the results list. */
export type HelpSearchGroup = {
  kind: HelpSearchResult['kind'];
  heading: string;
  results: HelpSearchResult[];
};

/**
 * Bucket results into their sections, keeping [KIND_RANK] order and dropping
 * sections with nothing in them. Results arrive already sorted, so each group's
 * own order is preserved.
 */
export function groupHelpResults(
  results: HelpSearchResult[]
): HelpSearchGroup[] {
  const byKind = new Map<HelpSearchResult['kind'], HelpSearchResult[]>();
  results.forEach((result) => {
    const bucket = byKind.get(result.kind) ?? [];
    bucket.push(result);
    byKind.set(result.kind, bucket);
  });

  return [...byKind.entries()]
    .sort(([a], [b]) => KIND_RANK[a] - KIND_RANK[b])
    .map(([kind, groupResults]) => ({
      kind,
      heading: KIND_HEADING[kind],
      results: groupResults,
    }));
}

/**
 * Descriptions and FAQ answers are markdown. Flatten them to plain text so a
 * search for "login.gov" isn't defeated by link syntax, and so snippets don't
 * render raw markup.
 */
function stripMarkdown(markdown: string): string {
  return (markdown || '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // [text](href) -> text
    .replace(/[#*_`>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Where a result lives is decided by its own href, not by which sidebar links
 * to it — the how to and admin sidebars both carry "Related FAQs" links, and
 * those are FAQs wherever they are listed. External guides (PDFs) have no
 * /help/info href to read, so they fall back to the section that listed them.
 */
function kindForHref(
  href: string,
  section?: string
): HelpSearchResult['kind'] {
  const path = href.startsWith('/help/info/')
    ? href.slice('/help/info/'.length)
    : section ?? '';
  if (path.startsWith('faq')) {
    return 'FAQ';
  }
  if (path.startsWith('how-to')) {
    return 'How To';
  }
  if (path.startsWith('admin')) {
    return 'Admin';
  }
  return 'Help';
}

/**
 * One flat list of everything a user can land on from the Help Center: the how
 * to / admin pages, the external guides linked from their sidebars, and every
 * individual FAQ. Built once at module load — the source data is static.
 */
function buildIndex(): IndexEntry[] {
  const entries: IndexEntry[] = [];
  const seenHrefs = new Set<string>();

  const add = (entry: Omit<IndexEntry, 'titleLower' | 'bodyLower'>) => {
    if (seenHrefs.has(entry.href)) {
      return;
    }
    seenHrefs.add(entry.href);
    entries.push({
      ...entry,
      titleLower: entry.title.toLowerCase(),
      bodyLower: entry.body.toLowerCase(),
    });
  };

  Object.keys(HelpPages).forEach((slug) => {
    const page = HelpPages[slug];
    const href = '/help/info/' + slug;
    add({
      title: page.title.trim(),
      href,
      kind: kindForHref(href),
      body: stripMarkdown(page.description),
    });
  });

  mainFaqMap.forEach((group) => {
    group.questions.forEach((question) => {
      add({
        title: question.question.trim(),
        href: '/help/info/faq/' + question.slug,
        kind: 'FAQ',
        body: stripMarkdown(question.answer),
      });
    });
  });

  // Indexed last, so this only picks up sidebar links with no page or FAQ of
  // their own — the Getting Started and Guest Login guides — rather than
  // shadowing the richer entries above with a title-only copy.
  Object.keys(TopicSidebars).forEach((section) => {
    TopicSidebars[section].forEach((item) => {
      if (item.type !== 'link') {
        return;
      }
      add({
        title: item.title.trim(),
        href: item.href,
        kind: kindForHref(item.href, section),
        external: item.external,
        body: '',
      });
    });
  });

  return entries;
}

const helpSearchIndex = buildIndex();

/** A short window of body text around the first term that matched. */
function buildSnippet(entry: IndexEntry, tokens: string[]): string | undefined {
  if (!entry.body) {
    return undefined;
  }
  const firstHit = tokens
    .map((token) => entry.bodyLower.indexOf(token))
    .filter((index) => index >= 0)
    .sort((a, b) => a - b)[0];
  if (firstHit === undefined) {
    return undefined;
  }
  const start = Math.max(0, firstHit - 40);
  const snippet = entry.body.slice(start, start + 140).trim();
  return (start > 0 ? '…' : '') + snippet + (start + 140 < entry.body.length ? '…' : '');
}

/**
 * Search the Help Center. Every term has to match somewhere in a page for it to
 * be returned; title matches rank above body matches, and a title that starts
 * with a term ranks above one that merely contains it.
 */
/** Split a query into the terms every result has to match. */
function tokenize(query: string): string[] {
  return (query || '')
    .toLowerCase()
    .split(/\s+/)
    .filter((token) => token.length > 0);
}

/**
 * Every entry matching all of the terms, ranked by relevance: title matches
 * above body matches, and a title that starts with a term above one that
 * merely contains it.
 */
function matchEntries(tokens: string[]): { entry: IndexEntry; score: number }[] {
  return helpSearchIndex
    .map((entry) => {
      let score = 0;
      for (const token of tokens) {
        const inTitle = entry.titleLower.includes(token);
        const inBody = entry.bodyLower.includes(token);
        if (!inTitle && !inBody) {
          return null;
        }
        if (inTitle) {
          score += 10;
          if (entry.titleLower.startsWith(token)) {
            score += 5;
          }
        }
        if (inBody) {
          score += 2;
        }
      }
      return { entry, score };
    })
    .filter((match) => match !== null)
    .sort(
      (a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title)
    );
}

/**
 * Search the Help Center.
 *
 * `limit` caps how many results come back — pass Infinity for all of them.
 * The cap is applied to the relevance ranking *before* results are grouped by
 * kind, so a handful of weak how to hits can never crowd out the FAQ someone
 * was actually after.
 */
export function searchHelp(
  query: string,
  limit: number = MAX_HELP_SEARCH_RESULTS
): HelpSearchResult[] {
  const tokens = tokenize(query);
  if (tokens.length === 0) {
    return [];
  }

  return matchEntries(tokens)
    .slice(0, limit)
    // Then group what survived: how to guides, FAQs, admin last.
    .sort(
      (a, b) =>
        KIND_RANK[a.entry.kind] - KIND_RANK[b.entry.kind] ||
        b.score - a.score ||
        a.entry.title.localeCompare(b.entry.title)
    )
    .map(({ entry }) => ({
      title: entry.title,
      href: entry.href,
      kind: entry.kind,
      external: entry.external,
      snippet: buildSnippet(entry, tokens),
    }));
}

/** How many entries match, ignoring any result cap. */
export function countHelpMatches(query: string): number {
  const tokens = tokenize(query);
  return tokens.length === 0 ? 0 : matchEntries(tokens).length;
}
